import { CognitoIdentityClient, GetIdCommand, GetCredentialsForIdentityCommand } from '@aws-sdk/client-cognito-identity';
import { getConfig } from '../config';

interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: number;
}

const TOKEN_STORAGE_KEY = 'eventstore-auth-tokens';
const CREDS_STORAGE_KEY = 'eventstore-identity-creds';
const PKCE_VERIFIER_KEY = 'eventstore-pkce-verifier';
const OAUTH_STATE_KEY = 'eventstore-oauth-state';

let cachedCredentials: AwsCredentials | null = null;

// --- PKCE helpers ---
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('').slice(0, length);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Redirect to Cognito Hosted UI for login */
export async function redirectToLogin(): Promise<void> {
  const config = getConfig();

  // PKCE: generate verifier and challenge
  const verifier = generateRandomString(64);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  const challenge = await generateCodeChallenge(verifier);

  // CSRF: generate state
  const state = generateRandomString(32);
  sessionStorage.setItem(OAUTH_STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    response_type: 'code',
    scope: 'openid email',
    redirect_uri: `${window.location.origin}/`,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });
  window.location.href = `https://${config.cognitoDomain}/oauth2/authorize?${params}`;
}

/** Redirect to Cognito Hosted UI for logout */
export function signOut(): void {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(CREDS_STORAGE_KEY);
  cachedCredentials = null;
  const config = getConfig();
  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    logout_uri: `${window.location.origin}/`,
  });
  window.location.href = `https://${config.cognitoDomain}/logout?${params}`;
}

/** Exchange authorization code for tokens via Cognito token endpoint */
async function exchangeCodeForTokens(code: string): Promise<AuthTokens> {
  const config = getConfig();
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);

  if (!verifier) {
    throw new Error('PKCE verifier missing — possible replay or CSRF attack');
  }

  const response = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.userPoolClientId,
      redirect_uri: `${window.location.origin}/`,
      code,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status}`);
  }

  const data = await response.json();
  const tokens: AuthTokens = {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  };
  sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  return tokens;
}

/** Get stored tokens if ID token still valid */
function getStoredTokens(): AuthTokens | null {
  const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (!stored) return null;
  try {
    const tokens: AuthTokens = JSON.parse(stored);
    if (tokens.expiresAt > Date.now() + 60_000) return tokens;
    // ID token expired but refresh token may still be valid
    if (tokens.refreshToken) return tokens;
  } catch { /* ignore */ }
  return null;
}

/** Use refresh token to get fresh ID/access tokens */
async function refreshTokensFromCognito(refreshToken: string): Promise<AuthTokens> {
  const config = getConfig();
  const response = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.userPoolClientId,
      refresh_token: refreshToken,
    }),
  });

  if (!response.ok) {
    throw new Error(`Token refresh failed: ${response.status}`);
  }

  const data = await response.json();
  const tokens: AuthTokens = {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken, // refresh token is not returned on refresh grant — reuse existing
    expiresAt: Date.now() + data.expires_in * 1000,
  };
  sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  return tokens;
}

/** Exchange ID token for AWS credentials via Identity Pool */
async function getCredentialsFromIdentityPool(idToken: string): Promise<AwsCredentials> {
  const config = getConfig();
  const client = new CognitoIdentityClient({ region: config.region });
  const providerName = `cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`;

  const { IdentityId } = await client.send(new GetIdCommand({
    IdentityPoolId: config.identityPoolId,
    Logins: { [providerName]: idToken },
  }));

  const { Credentials } = await client.send(new GetCredentialsForIdentityCommand({
    IdentityId,
    Logins: { [providerName]: idToken },
  }));

  if (!Credentials?.AccessKeyId || !Credentials.SecretKey || !Credentials.SessionToken) {
    throw new Error('Identity Pool returned incomplete credentials');
  }

  const creds: AwsCredentials = {
    accessKeyId: Credentials.AccessKeyId,
    secretAccessKey: Credentials.SecretKey,
    sessionToken: Credentials.SessionToken,
    expiration: Credentials.Expiration?.getTime() ?? Date.now() + 3600_000,
  };
  sessionStorage.setItem(CREDS_STORAGE_KEY, JSON.stringify(creds));
  cachedCredentials = creds;
  return creds;
}

/**
 * Initialize auth on app load.
 * Returns true if authenticated, false if redirecting to login.
 */
export async function initAuth(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const returnedState = params.get('state');

  if (code) {
    // Validate state (CSRF protection)
    const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    if (!expectedState || returnedState !== expectedState) {
      // State mismatch — possible CSRF, re-initiate login
      await redirectToLogin();
      return false;
    }

    // Clean URL
    window.history.replaceState({}, '', window.location.pathname);
    try {
      const tokens = await exchangeCodeForTokens(code);
      await getCredentialsFromIdentityPool(tokens.idToken);
      return true;
    } catch {
      await redirectToLogin();
      return false;
    }
  }

  // Check for existing valid tokens
  const tokens = getStoredTokens();
  if (tokens) {
    // Restore credentials from sessionStorage before hitting Identity Pool
    if (getCredentials()) {
      return true;
    }
    try {
      // If ID token expired, refresh it first
      let idToken = tokens.idToken;
      if (tokens.expiresAt <= Date.now() + 60_000) {
        const refreshed = await refreshTokensFromCognito(tokens.refreshToken);
        idToken = refreshed.idToken;
      }
      await getCredentialsFromIdentityPool(idToken);
      return true;
    } catch {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      await redirectToLogin();
      return false;
    }
  }

  await redirectToLogin();
  return false;
}

/** Get current AWS credentials for signing requests. Returns null if not authenticated. */
export function getCredentials(): { accessKeyId: string; secretAccessKey: string; sessionToken: string } | null {
  if (cachedCredentials && cachedCredentials.expiration > Date.now() + 60_000) {
    return cachedCredentials;
  }
  const stored = sessionStorage.getItem(CREDS_STORAGE_KEY);
  if (stored) {
    try {
      const creds: AwsCredentials = JSON.parse(stored);
      if (creds.expiration > Date.now() + 60_000) {
        cachedCredentials = creds;
        return creds;
      }
    } catch { /* ignore */ }
  }
  return null;
}

/** Refresh credentials (called on 403). Returns true if refreshed, false if re-login needed. */
export async function refreshCredentials(): Promise<boolean> {
  const tokens = getStoredTokens();
  if (!tokens) {
    await redirectToLogin();
    return false;
  }
  try {
    // If ID token expired, use refresh token to get a new one
    let idToken = tokens.idToken;
    if (tokens.expiresAt <= Date.now() + 60_000) {
      const refreshed = await refreshTokensFromCognito(tokens.refreshToken);
      idToken = refreshed.idToken;
    }
    await getCredentialsFromIdentityPool(idToken);
    return true;
  } catch {
    await redirectToLogin();
    return false;
  }
}
